import {
	sanitizeTiles,
	writeSavedWords,
	writeDictionary,
	isEmpty,
	mergeOldAndNewWords,
} from "@/common/functions";

import {
	DICTIONARIES_IN_API,
	GAME_TYPES,
	PAGES,
	INTERLINK_TYPE,
} from "@/common/consts";
import Vue from "vue";

export default {
	toggleBurger: function(state, isOpen) {
		state.navigation.burgerActive =
			typeof isOpen === "boolean"
				? isOpen
				: !state.navigation.burgerActive;
	},
	startLoading: function(state) {
		state.status.loading = true;
		state.status.error = false;
	},
	stopLoading: function(state) {
		state.status.loading = false;
	},
	errorFlag: function(state) {
		state.status.error = true;
	},
	/**
	 * @todo these 2 methods look very similar. Are they both needed?
	 */
	updateTiles: function(state, payload) {
		state.query.tiles = sanitizeTiles(payload.tiles, payload.routeName);
	},
	updateUnsortedTiles: function(state, payload) {
		state.query.unsortedTiles = sanitizeTiles(
			payload.tiles,
			payload.routeName
		);
	},
	updatePrefixedTiles: function(state, value) {
		state.query.prefixedTiles = value;
	},
	updateResultsExactMatch: function(state, value) {
		let match = value;
		try {
			match = {
				word: value.word,
				score: value.scores[0],
				mod: value.mod,
				has_definition: value.scores[value.scores.length - 1] === 1,
			};
		} catch {}

		Vue.set(state.results, "match", match);
	},
	updateResultsCount: function(state, value) {
		state.results.resultsCount = value;
	},
	updateSuggestedWordlistLinks: function(state, value) {
		state.results.suggestedLinks = value;
	},
	updateInterlinks: function(state, value) {
		if (value === undefined) return;

		// We have the particular issue that in the api, the interlink type for WITH is different than the term we use in FE. whoopsie!
		Vue.set(state.results.interlinks, INTERLINK_TYPE.STARTS, value.starts);
		Vue.set(state.results.interlinks, INTERLINK_TYPE.ENDS, value.ends);
		Vue.set(state.results.interlinks, INTERLINK_TYPE.WITH, value.contains);
		Vue.set(state.results.interlinks, INTERLINK_TYPE.LENGTH, value.length);
	},
	clearAdvancedSearch: function(state) {
		state.query.startsWith = "";
		state.query.endsWith = "";
		state.query.contains = "";
		state.query.wordLength = null;
	},
	clearAllTiles: function(state) {
		state.query.prefixedTiles = "";
		state.query.tiles = "";
		state.query.unsortedTiles = "";
	},
	updateStartsWith: function(state, value) {
		state.query.startsWith = value;
	},
	updateEndsWith: function(state, value) {
		state.query.endsWith = value;
	},
	updateContains: function(state, value) {
		if (value.match(/starts-/) || value.match(/ends-/)) {
			value = "";
		}
		state.query.contains = value;
	},
	updateWordlength: function(state, value) {
		state.query.wordLength = value;
	},
	updateCurrentPage: function(state, value) {
		// Is this not the first page that the user is visiting?
		if (state.status.justLanding && state.navigation.current)
			state.status.justLanding = false;
		state.navigation.current = value;
	},
	updateFinderHeight: function(state, value) {
		state.finderHeight = value;
	},
	updateLastVisited: function(state, value) {
		state.lastVisited = value;
	},
	loadSavedWords: function(state, payload) {
		state.savedWords = payload;
	},
	addSavedWord: function(state, payload) {
		let date = new Date().getTime();
		if (!Array.isArray(payload)) payload = [payload];
		payload.forEach((item) => {
			item.date = date;
		});
		state.savedWords = state.savedWords.concat(...payload);
		writeSavedWords(state.savedWords);
	},
	removeSavedWord: function(state, payload) {
		if (payload > -1) state.savedWords.splice(payload, 1);
		if (!Object.keys(state.savedWords).length) state.savedWords = [];
		writeSavedWords(state.savedWords);
	},
	resetSavedWordsFromLocalStorage: function(state) {
		state.savedWords = [];
	},
	setWords: function(state, payload) {
		//If a null payload is given, then we are resetting the words state.
		if (payload === null) {
			state.words = {};
			return;
		}

		let { gameType, data, offset } = payload;

		data = mergeOldAndNewWords(
			state.navigationChecks.currentRouteName ===
				PAGES.WORDS_BY_LENGTH_RESULTS
				? []
				: state.words,
			offset,
			gameType,
			data,
			payload.order_by
		);

		state.words = {
			...(state.words || {}),
			...data,
		};
	},
	setAnagrams: function(state, payload) {
		// If a null payload is given, then we are resetting the anagrams.
		if (payload === null) {
			state.anagrams = {};
			return;
		}

		const anagrams = mergeOldAndNewWords(
			state.anagrams,
			0,
			GAME_TYPES.UNSCRAMBLE,
			payload.data,
			"score"
		);

		state.anagrams = {
			...(state.anagrams || {}),
			...anagrams,
		};
	},
	setRelatedLists: function(state, value) {
		state.relatedLists = value;
	},
	setDictionary: function(state, value) {
		// Make sure dictionary value is valid.
		const isValidDictionary = Object.keys(DICTIONARIES_IN_API).some(
			(i) => DICTIONARIES_IN_API[i] === value
		);
		if (!isValidDictionary) return;
		state.query.dictionary = value;
		writeDictionary([{ dict: state.query.dictionary }]);
	},
	loadDictionary: function(state, value) {
		state.query.dictionary = value;
	},
	setOrderBy: function(state, value) {
		state.query.orderBy = value;
	},
	loadFilters: function(state, { dictionary }) {
		state.query.dictionary = dictionary;
	},
	setWordDefinition: function(state, value) {
		state.definition.wordDefinition = value;
	},
	setDefinitionType: function(state, value) {
		state.definition.wordType = value;
	},
	updateOpenedModals: function(state, value) {
		if (isEmpty(value)) {
			Vue.set(state, "openedModals", {});
		} else {
			Vue.set(state.openedModals, value.modalId, value.modalState);
		}
	},
	setLoadingSavedWords: function(state, value) {
		state.loadingSavedWords = value;
	},
	updateFocusedAdvancedField: function(state, payload) {
		Vue.set(
			state.query.focusedAdvanceFields,
			payload.fieldType,
			payload.focused
		);
	},
	setHideArticleAds: function(state, hideAds = false) {
		state.hideArticleAds = hideAds;
	},
};
